// DriverApp.tsx — Mobile-first Progressive Web App for drivers
// Route: /driver  (detected in index.tsx)
// Drivers log in with email + password created via the dispatcher's Driver Management room.
// Features: current load, status pipeline, photo upload (BOL/POD), HOS hours, check calls.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

// ─── Styles (mobile-first, dark theme matches dispatcher app) ─────────────────

const S = {
  root: { minHeight:'100dvh', background:'#000', color:'#fff', fontFamily:'monospace', userSelect:'none' as const },
  safe: { padding:'env(safe-area-inset-top, 12px) 16px env(safe-area-inset-bottom, 16px)' },
  header: { background:'#000', borderBottom:'1px solid #1a1a1a', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky' as const, top:0, zIndex:100 },
  logo: { color:'#32CD32', fontWeight:700, letterSpacing:'2px', fontSize:'13px' },
  card: { background:'#050505', border:'1px solid #1a1a1a', borderRadius:0, padding:'16px', marginBottom:'12px' },
  cardGreen: { background:'#050505', border:'1px solid #32CD32', padding:'16px', marginBottom:'12px' },
  label: { color:'#555', fontSize:'10px', letterSpacing:'1px', fontWeight:700, marginBottom:'4px' },
  val: { color:'#fff', fontSize:'16px', fontWeight:700 },
  smallVal: { color:'#aaa', fontSize:'13px' },
  input: { background:'#000', border:'1px solid #333', color:'#fff', padding:'14px', fontFamily:'monospace', fontSize:'15px', width:'100%', boxSizing:'border-box' as const, outline:'none', borderRadius:0 },
  btnGreen: { background:'#32CD32', color:'#000', border:'none', padding:'16px', fontWeight:700, fontFamily:'monospace', fontSize:'13px', letterSpacing:'1px', width:'100%', cursor:'pointer', borderRadius:0 },
  btnOutline: { background:'transparent', color:'#32CD32', border:'1px solid #32CD32', padding:'14px', fontWeight:700, fontFamily:'monospace', fontSize:'12px', letterSpacing:'1px', cursor:'pointer', borderRadius:0 },
  btnRed: { background:'#cc3333', color:'#fff', border:'none', padding:'16px', fontWeight:700, fontFamily:'monospace', fontSize:'13px', letterSpacing:'1px', width:'100%', cursor:'pointer', borderRadius:0 },
  btnGray: { background:'#111', color:'#555', border:'1px solid #222', padding:'14px', fontWeight:700, fontFamily:'monospace', fontSize:'12px', letterSpacing:'1px', cursor:'pointer', borderRadius:0 },
  sectionTitle: { color:'#32CD32', fontSize:'10px', letterSpacing:'2px', fontWeight:700, marginBottom:'12px' },
  divider: { borderColor:'#1a1a1a', margin:'16px 0' },
  pill: (active: boolean, color = '#32CD32') => ({
    display:'inline-block', padding:'4px 10px', fontSize:'10px', fontWeight:700, letterSpacing:'1px',
    border:`1px solid ${active ? color : '#333'}`, color:active ? color : '#555', background:'transparent',
  }),
  hosBar: (pct: number) => ({
    height:'6px', background:`linear-gradient(to right, #32CD32 ${pct}%, #1a1a1a ${pct}%)`, marginTop:'6px',
  }),
};

const WORKFLOW = [
  { key:'BOOKED',            label:'Booked',          short:'BOOKED' },
  { key:'DISPATCHED',        label:'Dispatched',      short:'DISPATCHED' },
  { key:'EN_ROUTE_PICKUP',   label:'En Route Pickup', short:'EN ROUTE' },
  { key:'AT_PICKUP',         label:'At Pickup',       short:'AT PICKUP' },
  { key:'LOADED',            label:'Loaded',          short:'LOADED' },
  { key:'EN_ROUTE_DELIVERY', label:'En Route Delivery', short:'EN ROUTE DEL' },
  { key:'AT_DELIVERY',       label:'At Delivery',     short:'AT DELIVERY' },
  { key:'DELIVERED',         label:'Delivered',       short:'DELIVERED' },
];

const STATUS_MAP: Record<string, string> = {
  BOOKED:'ACTIVE', DISPATCHED:'ACTIVE', EN_ROUTE_PICKUP:'IN TRANSIT',
  AT_PICKUP:'IN TRANSIT', LOADED:'IN TRANSIT', EN_ROUTE_DELIVERY:'IN TRANSIT',
  AT_DELIVERY:'IN TRANSIT', DELIVERED:'DELIVERED',
};

const fmtDate = (d: string) => { try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' }); } catch { return d || '—'; } };
const hosHM  = (secs: number | null) => { if (!secs) return '—'; const h = Math.floor(secs/3600), m = Math.floor((secs%3600)/60); return `${h}h ${m}m`; };

// ─── Sub-components ───────────────────────────────────────────────────────────

function HOSBar({ driver }: { driver: any }) {
  const drive = Number(driver.hos_drive_remaining_sec || 0);
  const maxDrive = 11 * 3600;
  const pct = Math.min(100, Math.round((drive / maxDrive) * 100));
  const color = pct > 40 ? '#32CD32' : pct > 20 ? '#FFD700' : '#cc3333';
  return (
    <div style={S.card}>
      <div style={S.sectionTitle}>HOS STATUS</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        <div>
          <div style={S.label}>DRIVE TIME LEFT</div>
          <div style={{ ...S.val, color }}>{hosHM(drive)}</div>
          <div style={{ height:'6px', background:`linear-gradient(to right, ${color} ${pct}%, #1a1a1a ${pct}%)`, marginTop:'6px' }} />
        </div>
        <div>
          <div style={S.label}>DUTY STATUS</div>
          <div style={{ ...S.val, fontSize:'14px' }}>{driver.hos_status || 'OFF DUTY'}</div>
          <div style={{ ...S.smallVal, marginTop:'4px' }}>{driver.current_city ? `${driver.current_city}, ${driver.current_state}` : 'Location unknown'}</div>
        </div>
      </div>
    </div>
  );
}

function LoadCard({ load, driver, onAdvance, advancing }: { load: any; driver: any; onAdvance: (s: string) => void; advancing: boolean }) {
  const wfIdx = WORKFLOW.findIndex(s => s.key === load.workflow_status);
  const currentStage = WORKFLOW[wfIdx] || WORKFLOW[0];
  const nextStage = WORKFLOW[wfIdx + 1];

  return (
    <div style={S.cardGreen}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
        <div>
          <div style={S.label}>CURRENT LOAD</div>
          <div style={{ color:'#32CD32', fontSize:'22px', fontWeight:700 }}>#{load.load_number}</div>
          <div style={{ color:'#aaa', fontSize:'12px' }}>{load.customer_name || 'No customer'}</div>
        </div>
        <span style={S.pill(true)}>{currentStage.short}</span>
      </div>

      {/* Route */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'8px', alignItems:'center', marginBottom:'16px' }}>
        <div>
          <div style={S.label}>PICKUP</div>
          <div style={{ color:'#fff', fontWeight:700, fontSize:'14px' }}>{load.pickup_city || '—'}, {load.pickup_state || ''}</div>
          <div style={{ color:'#555', fontSize:'11px' }}>{fmtDate(load.pickup_date)}</div>
        </div>
        <div style={{ color:'#32CD32', fontSize:'18px' }}>→</div>
        <div style={{ textAlign:'right' as const }}>
          <div style={S.label}>DELIVERY</div>
          <div style={{ color:'#fff', fontWeight:700, fontSize:'14px' }}>{load.delivery_city || '—'}, {load.delivery_state || ''}</div>
          <div style={{ color:'#555', fontSize:'11px' }}>{fmtDate(load.delivery_date)}</div>
        </div>
      </div>

      {/* Progress pipeline */}
      <div style={{ overflowX:'auto' as const, paddingBottom:'8px', marginBottom:'16px' }}>
        <div style={{ display:'flex', gap:'4px', minWidth:'max-content' }}>
          {WORKFLOW.map((stage, i) => (
            <div key={stage.key} style={{ textAlign:'center' as const, minWidth:'60px' }}>
              <div style={{ width:'24px', height:'24px', borderRadius:'50%', margin:'0 auto 4px',
                background: i < wfIdx ? '#32CD32' : i === wfIdx ? '#32CD32' : '#1a1a1a',
                border: i === wfIdx ? '2px solid #fff' : '2px solid transparent',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', color: i <= wfIdx ? '#000' : '#333' }}>
                {i < wfIdx ? '✓' : i + 1}
              </div>
              <div style={{ fontSize:'8px', color: i === wfIdx ? '#32CD32' : '#333', letterSpacing:'0.5px' }}>{stage.short.split(' ')[0]}</div>
            </div>
          ))}
        </div>
      </div>

      {load.special_instructions && (
        <div style={{ background:'#0a0a0a', border:'1px solid #333', padding:'10px', marginBottom:'16px', fontSize:'12px', color:'#aaa' }}>
          <span style={{ color:'#FFD700', fontWeight:700 }}>⚠ </span>{load.special_instructions}
        </div>
      )}

      {nextStage && (
        <button
          onClick={() => onAdvance(nextStage.key)}
          disabled={advancing}
          style={{ ...S.btnGreen, fontSize:'14px', padding:'18px' }}
        >
          {advancing ? 'UPDATING...' : `→ MARK ${nextStage.label.toUpperCase()}`}
        </button>
      )}
      {!nextStage && load.workflow_status === 'DELIVERED' && (
        <div style={{ textAlign:'center' as const, color:'#32CD32', padding:'16px', fontWeight:700 }}>✓ LOAD COMPLETE</div>
      )}
    </div>
  );
}

function PhotoUpload({ load, driver, companyId }: { load: any; driver: any; companyId: string }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState<'BOL'|'POD'|'PHOTO'>('BOL');
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState<any[]>([]);
  const [uploaded, setUploaded] = useState(false);

  const fetchDocs = useCallback(async () => {
    const { data } = await supabase.from('load_documents').select('*').eq('load_id', load.id).order('created_at', { ascending: false });
    setDocs(data || []);
  }, [load.id]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${companyId}/${load.id}/${docType}_${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('load-documents').upload(path, file, { upsert: false });
    if (upErr) { alert(`Upload failed: ${upErr.message}`); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('load-documents').getPublicUrl(path);
    await supabase.from('load_documents').insert({ company_id:companyId, load_id:load.id, driver_id:driver.id, doc_type:docType, storage_path:path, public_url:publicUrl });
    setUploading(false); setUploaded(true); fetchDocs();
    setTimeout(() => setUploaded(false), 2000);
    if (fileRef.current) fileRef.current.value = '';
    // Trigger OCR in background
    supabase.functions.invoke('ocr-document', { body: { documentId: (await supabase.from('load_documents').select('id').eq('load_id', load.id).eq('doc_type', docType).order('created_at', { ascending: false }).limit(1).single().then(r => r.data))?.id, publicUrl, docType, loadId: load.id } }).catch(() => {});
  };

  return (
    <div style={S.card}>
      <div style={S.sectionTitle}>DOCUMENTS & PHOTOS</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'12px' }}>
        {(['BOL','POD','PHOTO'] as const).map(t => (
          <button key={t} onClick={() => setDocType(t)} style={{ ...(docType===t ? S.btnGreen : S.btnGray), padding:'10px', fontSize:'11px', width:'100%' }}>{t}</button>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment" onChange={handleUpload} style={{ display:'none' }} />
      <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...S.btnOutline, width:'100%', marginBottom:'12px' }}>
        {uploading ? 'UPLOADING...' : uploaded ? '✓ UPLOADED' : `📷 CAPTURE / UPLOAD ${docType}`}
      </button>
      {docs.length > 0 && (
        <div>
          <div style={{ color:'#555', fontSize:'10px', marginBottom:'8px' }}>UPLOADED ({docs.length})</div>
          {docs.map(d => (
            <div key={d.id} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #111', fontSize:'12px' }}>
              <span style={{ color:'#32CD32', fontWeight:700 }}>{d.doc_type}</span>
              <a href={d.public_url} target="_blank" rel="noreferrer" style={{ color:'#aaa', textDecoration:'none' }}>View ↗</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main DriverApp ───────────────────────────────────────────────────────────

type Screen = 'login' | 'home' | 'load' | 'notes';

// Haversine distance in miles
function geoMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export default function DriverApp() {
  const [screen, setScreen] = useState<Screen>('login');
  const [session, setSession] = useState<any>(null);
  const [driver, setDriver] = useState<any>(null);
  const [activeLoad, setActiveLoad] = useState<any>(null);
  const [allLoads, setAllLoads] = useState<any[]>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [notes, setNotes] = useState<any[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const [geoToast, setGeoToast] = useState<string | null>(null);
  const autoAdvancedRef = useRef<Set<string>>(new Set());
  const watchIdRef = useRef<number | null>(null);

  // Auth listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { if (data.session) { setSession(data.session); setScreen('home'); } });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => { setSession(s); if (s) setScreen('home'); else setScreen('login'); });
    return () => listener.subscription.unsubscribe();
  }, []);

  // Request push notification permission and register subscription
  useEffect(() => {
    if (!session || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    Notification.requestPermission().then(async perm => {
      if (perm !== 'granted') return;
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY });
        const uid = session.user.id;
        const { data: drv } = await supabase.from('drivers').select('id,company_id').eq('auth_user_id', uid).maybeSingle();
        if (drv) await supabase.from('push_subscriptions').upsert({ driver_id: drv.id, company_id: drv.company_id, subscription: sub.toJSON() }, { onConflict: 'driver_id' });
      } catch { /* push not supported or blocked */ }
    });
  }, [session]);

  // Load driver profile + loads
  useEffect(() => {
    if (!session) return;
    const uid = session.user.id;
    supabase.from('drivers').select('*').eq('auth_user_id', uid).maybeSingle().then(({ data }) => {
      if (!data) { supabase.from('drivers').select('*').eq('email', session.user.email).maybeSingle().then(({ data: d2 }) => { if (d2) setDriver(d2); }); }
      else setDriver(data);
    });
  }, [session]);

  useEffect(() => {
    if (!driver) return;
    supabase.from('loads')
      .select('*')
      .eq('assigned_driver_id', driver.id)
      .not('status', 'eq', 'CANCELLED')
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        setAllLoads(data || []);
        const active = (data || []).find((l: any) => ['ACTIVE','IN TRANSIT'].includes(l.status));
        setActiveLoad(active || data?.[0] || null);
      });
  }, [driver]);

  const handleLogin = async () => {
    setLoggingIn(true); setLoginErr('');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) { setLoginErr(error.message); setLoggingIn(false); }
  };

  const handleAdvance = async (nextWf: string) => {
    if (!activeLoad) return;
    setAdvancing(true);
    const updates: any = { workflow_status: nextWf, status: STATUS_MAP[nextWf] || activeLoad.status };
    if (nextWf === 'DISPATCHED') updates.dispatched_at = new Date().toISOString();
    if (nextWf === 'DELIVERED') updates.delivered_at = new Date().toISOString();
    const { data: updated } = await supabase.from('loads').update(updates).eq('id', activeLoad.id).select('*').single();
    if (updated) setActiveLoad(updated);
    setAdvancing(false);
  };

  // GPS geofencing — auto-advance when driver arrives at pickup or delivery
  useEffect(() => {
    if (!activeLoad || !navigator.geolocation) return;
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const wf = activeLoad.workflow_status || '';
        const key = `${activeLoad.id}:${wf}`;
        if (autoAdvancedRef.current.has(key)) return;

        // Arrived at pickup
        if (wf === 'EN_ROUTE_PICKUP' && activeLoad.pickup_lat && activeLoad.pickup_lng) {
          const dist = geoMiles(latitude, longitude, Number(activeLoad.pickup_lat), Number(activeLoad.pickup_lng));
          if (dist < 0.3) {
            autoAdvancedRef.current.add(key);
            setGeoToast('📍 Arrived at pickup — updating status...');
            setTimeout(() => { handleAdvance('AT_PICKUP'); setGeoToast(null); }, 3000);
          }
        }

        // Departed pickup with load (moved >0.5mi away while AT_PICKUP)
        if (wf === 'AT_PICKUP' && activeLoad.pickup_lat && activeLoad.pickup_lng) {
          const dist = geoMiles(latitude, longitude, Number(activeLoad.pickup_lat), Number(activeLoad.pickup_lng));
          if (dist > 0.5) {
            autoAdvancedRef.current.add(key);
            setGeoToast('🚛 Departed pickup — marking loaded...');
            setTimeout(() => { handleAdvance('LOADED'); setGeoToast(null); }, 3000);
          }
        }

        // Arrived at delivery
        if (['LOADED', 'EN_ROUTE_DELIVERY'].includes(wf) && activeLoad.delivery_lat && activeLoad.delivery_lng) {
          const dist = geoMiles(latitude, longitude, Number(activeLoad.delivery_lat), Number(activeLoad.delivery_lng));
          if (dist < 0.3) {
            autoAdvancedRef.current.add(key);
            setGeoToast('📍 Arrived at delivery — updating status...');
            setTimeout(() => { handleAdvance('AT_DELIVERY'); setGeoToast(null); }, 3000);
          }
        }
      },
      () => { /* GPS unavailable — silent fail, driver uses buttons */ },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 }
    );

    return () => { if (watchIdRef.current !== null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; } };
  }, [activeLoad?.id, activeLoad?.workflow_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotes = useCallback(async () => {
    if (!activeLoad) return;
    const { data } = await supabase.from('load_notes').select('*').eq('load_id', activeLoad.id).order('created_at', { ascending: false });
    setNotes(data || []);
  }, [activeLoad]);

  useEffect(() => { if (screen === 'notes') fetchNotes(); }, [screen, fetchNotes]);

  const handleSaveNote = async () => {
    if (!noteDraft.trim() || !activeLoad || !driver) return;
    setSavingNote(true);
    await supabase.from('load_notes').insert({ load_id:activeLoad.id, author_type:'driver', author_label:driver.full_name || 'Driver', note:noteDraft.trim() });
    setNoteDraft(''); fetchNotes(); setSavingNote(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (screen === 'login') return (
    <div style={{ ...S.root, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', minHeight:'100dvh', padding:'24px' }}>
      <div style={{ width:'100%', maxWidth:'360px' }}>
        <div style={{ textAlign:'center' as const, marginBottom:'40px' }}>
          <div style={{ color:'#32CD32', fontSize:'20px', fontWeight:700, letterSpacing:'4px' }}>BLACKTOP</div>
          <div style={{ color:'#555', fontSize:'11px', letterSpacing:'2px', marginTop:'4px' }}>DRIVER PORTAL</div>
        </div>
        <div style={S.label}>EMAIL</div>
        <input type="email" inputMode="email" placeholder="your@email.com" style={{ ...S.input, marginBottom:'12px' }} value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleLogin()} />
        <div style={S.label}>PASSWORD</div>
        <input type="password" placeholder="••••••••" style={{ ...S.input, marginBottom:'20px' }} value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleLogin()} />
        {loginErr && <div style={{ color:'#cc3333', fontSize:'12px', marginBottom:'12px' }}>{loginErr}</div>}
        <button onClick={handleLogin} disabled={loggingIn} style={S.btnGreen}>{loggingIn ? 'SIGNING IN...' : 'SIGN IN'}</button>
        <div style={{ color:'#333', fontSize:'11px', textAlign:'center' as const, marginTop:'20px' }}>Contact your dispatcher for login credentials</div>
      </div>
    </div>
  );

  return (
    <div style={S.root}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.logo}>BLACKTOP</div>
          {driver && <div style={{ color:'#555', fontSize:'10px', marginTop:'2px' }}>{driver.full_name}</div>}
        </div>
        <button onClick={() => supabase.auth.signOut()} style={{ background:'transparent', border:'none', color:'#555', fontSize:'11px', cursor:'pointer', fontFamily:'monospace' }}>SIGN OUT</button>
      </div>

      {/* Bottom nav */}
      <div style={{ position:'fixed' as const, bottom:0, left:0, right:0, background:'#000', borderTop:'1px solid #1a1a1a', display:'grid', gridTemplateColumns:'repeat(3,1fr)', zIndex:100 }}>
        {([['home','HOME'],['load','LOAD'],['notes','NOTES']] as const).map(([s,l]) => (
          <button key={s} onClick={() => setScreen(s)} style={{ background:'transparent', border:'none', color:screen===s?'#32CD32':'#555', padding:'14px 8px', fontFamily:'monospace', fontSize:'10px', letterSpacing:'1px', fontWeight:700, cursor:'pointer', borderTop:screen===s?'2px solid #32CD32':'2px solid transparent' }}>{l}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding:'16px 16px 80px', maxWidth:'600px', margin:'0 auto' }}>

        {/* GPS auto-advance toast */}
        {geoToast && (
          <div style={{ position:'fixed', top:'80px', left:'16px', right:'16px', background:'#32CD32', color:'#000', padding:'14px 16px', fontFamily:'monospace', fontWeight:700, fontSize:'13px', zIndex:999, textAlign:'center' as const }}>
            {geoToast}
          </div>
        )}

        {screen === 'home' && (
          <>
            {driver && <HOSBar driver={driver} />}
            <div style={S.card}>
              <div style={S.sectionTitle}>MY LOADS</div>
              {allLoads.length === 0 && <div style={{ color:'#555', fontSize:'13px' }}>No loads assigned yet.</div>}
              {allLoads.map(l => (
                <div key={l.id} onClick={() => { setActiveLoad(l); setScreen('load'); }}
                  style={{ borderBottom:'1px solid #111', padding:'12px 0', cursor:'pointer' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ color:'#32CD32', fontWeight:700, fontSize:'15px' }}>#{l.load_number}</div>
                      <div style={{ color:'#aaa', fontSize:'12px' }}>{l.pickup_city} → {l.delivery_city}</div>
                    </div>
                    <div style={{ textAlign:'right' as const }}>
                      <span style={S.pill(['ACTIVE','IN TRANSIT'].includes(l.status), l.status==='DELIVERED'?'#555':'#32CD32')}>{l.workflow_status || l.status}</span>
                      <div style={{ color:'#555', fontSize:'10px', marginTop:'4px' }}>{fmtDate(l.delivery_date)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {screen === 'load' && (
          <>
            {activeLoad
              ? <>
                  <LoadCard load={activeLoad} driver={driver} onAdvance={handleAdvance} advancing={advancing} />
                  {driver && <PhotoUpload load={activeLoad} driver={driver} companyId={driver.company_id} />}
                  <div style={S.card}>
                    <div style={S.sectionTitle}>LOAD DETAILS</div>
                    {[
                      ['Shipper', activeLoad.shipper_name], ['Shipper Addr', activeLoad.shipper_address],
                      ['Consignee', activeLoad.consignee_name], ['Consignee Addr', activeLoad.consignee_address],
                      ['PO Number', activeLoad.po_number], ['Equipment', activeLoad.equipment_type],
                      ['Weight', activeLoad.weight_lbs ? `${Number(activeLoad.weight_lbs).toLocaleString()} lbs` : null],
                      ['Miles', activeLoad.loaded_miles ? `${activeLoad.loaded_miles} mi` : null],
                    ].filter(([,v]) => v).map(([k, v]) => (
                      <div key={String(k)} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #0a0a0a', fontSize:'12px' }}>
                        <span style={{ color:'#555' }}>{k}</span>
                        <span style={{ color:'#aaa', textAlign:'right' as const, maxWidth:'60%' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </>
              : <div style={{ color:'#555', padding:'40px', textAlign:'center' as const }}>No load selected. Go to Home to pick a load.</div>
            }
          </>
        )}

        {screen === 'notes' && (
          <div style={S.card}>
            <div style={S.sectionTitle}>LOAD NOTES {activeLoad ? `— #${activeLoad.load_number}` : ''}</div>
            {!activeLoad && <div style={{ color:'#555', fontSize:'12px' }}>Select a load first.</div>}
            {activeLoad && (
              <>
                <textarea
                  placeholder="Type a note for dispatch..."
                  style={{ ...S.input, height:'100px', resize:'vertical' as const, marginBottom:'10px' }}
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                />
                <button onClick={handleSaveNote} disabled={savingNote || !noteDraft.trim()} style={{ ...S.btnGreen, marginBottom:'16px' }}>
                  {savingNote ? 'SAVING...' : 'SEND TO DISPATCH'}
                </button>
                <div style={{ color:'#555', fontSize:'10px', marginBottom:'12px' }}>PREVIOUS NOTES</div>
                {notes.length === 0 && <div style={{ color:'#333', fontSize:'12px' }}>No notes yet.</div>}
                {notes.map(n => (
                  <div key={n.id} style={{ borderBottom:'1px solid #111', padding:'10px 0' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                      <span style={{ color:'#32CD32', fontSize:'10px', fontWeight:700 }}>{n.author_label}</span>
                      <span style={{ color:'#333', fontSize:'10px' }}>{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ color:'#aaa', fontSize:'12px' }}>{n.note}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
